// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Button,
  ListItem,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
import usePageTitle from "app/pageTitle";
import AppConfig from "appConfig";

function PSAFeedbacksPage() {
  const pageTitle = "Product Sampling - Feedbacks";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [feedbacks, setFeedbacks] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const tableColumns = [
    { Header: "#", accessor: "action", align: "left" },
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Submitted On", accessor: "created", align: "right" },
  ];

  const buildRateRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        name: row.sample_record,
        action: (
          <MDBox>
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              iconOnly
              onClick={() => {
                navigate(`/campaigns/product-sampling/feedbacks/view/${row.reference}`);
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="info">
                visibility
              </Icon>
            </MDButton>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/psa/${ref}/feedbacks.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildRateRows(result.data.data.feedbacks);
            setFeedbacks(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function getFeedback(reference) {
    setShowLoading(true);
    await axios
      .get(`portal/psa/feedbacks/${reference}/fetch.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setFeedback(result.data.data.feedback);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {feedback && (
        <Dialog open fullWidth maxWidth="sm">
          <DialogTitle></DialogTitle>
          <DialogContent>
            <MDBox>
              <MDBox align="center">
                <MDTypography variant="subtitle2">Feedback Record</MDTypography>
              </MDBox>
              <MDBox align="center">
                <MDTypography variant="h6">{feedback.name}</MDTypography>
              </MDBox>
              <MDBox mt={2} align="center">
                <img src={`${AppConfig.api_media_url}${feedback.image}`} />
              </MDBox>
              <MDBox mt={3}>
                {feedback.products.map((row) => (
                  <MDBox mb={2}>
                    <ListItem divider>
                      <ListItemText
                        primary={<MDTypography variant="subtitle2">{row.name}</MDTypography>}
                        secondary={
                          <MDTypography
                            variant="subtitle2"
                            fontSize={12}
                          >{`Sampled Quantity: ${row.quantity}`}</MDTypography>
                        }
                      />
                    </ListItem>
                  </MDBox>
                ))}
              </MDBox>
              <MDBox mt={3}>
                <ListItem divider>
                  <ListItemText
                    primary={<MDTypography variant="subtitle2">GPS</MDTypography>}
                    secondary={
                      <MDTypography
                        variant="subtitle2"
                        fontSize={12}
                      >{`Lat: ${feedback.lat} | Lng: ${feedback.lng}`}</MDTypography>
                    }
                  />
                  <MDBox align="center">
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="error"
                      iconOnly
                      onClick={() => {
                        const url = `https://maps.google.com?q=${feedback.lat},${feedback.lng}`;
                        window.open(url, "_blank").focus();
                      }}
                    >
                      <Icon sx={{ cursor: "pointer" }} fontSize="small" color="error">
                        location_on
                      </Icon>
                    </MDButton>
                  </MDBox>
                </ListItem>
              </MDBox>
              <MDBox mt={3}>
                <ListItem divider>
                  <ListItemText
                    primary={<MDTypography variant="subtitle2">Sampled On</MDTypography>}
                    secondary={
                      <MDTypography variant="subtitle2" fontSize={12}>
                        {feedback.created}
                      </MDTypography>
                    }
                  />
                </ListItem>
              </MDBox>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="outlined"
              color="dark"
              iconOnly
              circular
              onClick={() => setFeedback(null)}
            >
              <Icon>close</Icon>
            </MDButton>
          </DialogActions>
        </Dialog>
      )}
      <MDBox mb={5}>
        <MDBox mt={1} mb={2} display="flex" justifyContent="space-between">
          <MDBox>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                navigate(`/campaigns/product-sampling/${ref}`);
              }}
            >
              Back
            </Button>
          </MDBox>
        </MDBox>
        {pageLoaded && (
          <MDBox mt={2}>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={0}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox p={2}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Feedback Records
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox mt={1}>
                  <DataTable
                    table={{ columns: tableColumns, rows: feedbacks }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    noEndBorder
                  />
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PSAFeedbacksPage;
